import React, { FC, useMemo } from 'react';
import { Collapse } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AthleteAttendanceTeam } from '../../models/attendance.models';
import { PeriodBreakdownItem, WeekBreakdownItem } from '../../models/stats.models';
import { formatSeasonDurationHours } from '../../pages/training-log/training-log.helpers';
import { getElevationMeasurementUnit, getMeasurementUnit, ZONE_CONFIGS } from '../../helpers/general.helpers';
import PeriodDetailBreakdown from './period-detail-breakdown.component';
import {
  buildBarSegments,
  formatApiDateDisplay,
  formatPeriodDateRangeLabel,
  formatVsPrevKpiLabel,
  formatVsPrevKpiValue,
  formatZoneDistanceCell,
  formatZoneDurationCell,
  getHourDelta,
  getPeriodStatus,
  getPeriodTitle,
  getUpcomingPeriodStartLabel,
  getVsPrevBar,
  groupWeeksByPeriod,
  isCurrentWeek,
} from './period-summary.helpers';

interface PeriodAccordionProps {
  period: PeriodBreakdownItem;
  previousPeriod: PeriodBreakdownItem | null;
  weeks: WeekBreakdownItem[];
  expanded: boolean;
  onToggle: () => void;
  attendanceTeams?: AthleteAttendanceTeam[] | null;
  attendanceLoading?: boolean;
  onNavigateToTrainingLog?: (period: number, week: number) => void;
}

const PeriodAccordion: FC<PeriodAccordionProps> = ({
  period,
  previousPeriod,
  weeks,
  expanded,
  onToggle,
  attendanceTeams,
  attendanceLoading,
  onNavigateToTrainingLog,
}) => {
  const navigate = useNavigate();
  const distanceUnit = getMeasurementUnit();
  const elevationUnit = getElevationMeasurementUnit();
  const status = useMemo(() => getPeriodStatus(period, weeks), [period, weeks]);
  const periodWeeks = useMemo(() => groupWeeksByPeriod(weeks, period.index), [weeks, period.index]);
  const isUpcoming = status === 'upcoming' && (period.duration?.total ?? 0) === 0;

  const zoneBarSegments = buildBarSegments(
    period.totalDurationBreakdown ?? [],
    ZONE_CONFIGS.map((z) => z.barColor)
  );

  const vsPrevBar = getVsPrevBar(period.duration?.percentDiff ?? 0);
  const hourDelta = getHourDelta(period.duration?.total ?? 0, previousPeriod?.duration?.total ?? 0);

  return (
    <div className={`period-accordion ${status === 'current' ? 'pa-current' : ''} ${isUpcoming ? 'pa-empty' : ''}`}>
      <div
        className="pa-header"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className="pa-title-col">
          <div className="pa-num">
            {getPeriodTitle(period, status)}
            {status === 'current' && <span className="pa-current-badge">Current</span>}
          </div>
          <div className="pa-dates">{formatPeriodDateRangeLabel(period.period)}</div>
        </div>
        <div className="pa-details-link">
          Details <span className={`pa-dl-arrow ${expanded ? 'open' : ''}`}>▾</span>
        </div>
        {isUpcoming ? (
          <div className="pa-mini-bar pa-empty-bar" />
        ) : (
          <div className="pa-mini-bar">
            {zoneBarSegments.map((segment, index) => (
              <span key={index} style={{ background: segment.color, flex: segment.flex }} />
            ))}
          </div>
        )}
        <div className={`pa-hours ${isUpcoming ? 'pa-muted' : ''}`}>
          {isUpcoming ? '—' : formatSeasonDurationHours(period.duration?.total ?? 0)}
        </div>
        <div className={`pa-dist ${isUpcoming ? 'pa-muted' : ''}`}>
          {isUpcoming ? (
            '—'
          ) : (
            <>
              {Math.round(period.distance?.total ?? 0)}
              <span>{distanceUnit}</span>
            </>
          )}
        </div>
      </div>

      <Collapse in={expanded}>
        <div className="pa-body">
          {isUpcoming ? (
            <div className="pa-empty-state">
              <div className="pa-empty-icon">◉</div>
              <div className="pa-empty-title">No training data yet</div>
              <div className="pa-empty-desc">
                This period starts {getUpcomingPeriodStartLabel(period)}. Data will appear here as workouts are logged.
              </div>
            </div>
          ) : (
            <>
              <div className="pd-kpi-strip">
                <div className="pd-kpi">
                  <div className="kpi-val">{formatSeasonDurationHours(period.duration?.weeklyAverage ?? 0)}</div>
                  <div className="kpi-lbl">Avg/wk</div>
                </div>
                <div className="pd-kpi">
                  <div className="kpi-val">{Math.round(period.distance?.total ?? 0).toLocaleString()}</div>
                  <div className="kpi-lbl">Distance ({distanceUnit})</div>
                </div>
                <div className="pd-kpi">
                  <div className="kpi-val">{(period.elevationGain?.total ?? 0).toLocaleString()}</div>
                  <div className="kpi-lbl">Ascent ({elevationUnit})</div>
                </div>
                <div className="pd-kpi">
                  <div className="kpi-val">
                    {(period.effortRatings?.average ?? 0) > 0 ? (period.effortRatings?.average ?? 0).toFixed(1) : '—'}
                  </div>
                  <div className="kpi-lbl">Avg Effort</div>
                </div>
                <div className="pd-kpi">
                  <div className="kpi-val">{formatVsPrevKpiValue(period.duration?.percentDiff ?? 0)}</div>
                  <div className="kpi-lbl">
                    {formatVsPrevKpiLabel(period.index, period.duration?.percentDiff ?? 0, hourDelta)}
                  </div>
                </div>
              </div>

              <div className="pd-planned-vs">
                <span
                  style={{
                    color: 'var(--color-text-secondary, #6b7280)',
                    fontWeight: 500,
                    width: 72,
                    flexShrink: 0,
                    fontSize: 11,
                  }}
                >
                  vs. Prev
                </span>
                <div className="pvs-bar">
                  <div className="pvs-fill" style={{ width: `${vsPrevBar.fillWidth}%`, background: vsPrevBar.color }} />
                </div>
                <span style={{ fontWeight: 600, fontSize: 11, color: vsPrevBar.color, width: 40, textAlign: 'right' }}>
                  {vsPrevBar.label}
                </span>
              </div>

              <PeriodDetailBreakdown
                period={period}
                attendanceTeams={attendanceTeams}
                attendanceLoading={attendanceLoading}
              />

              <div className="pd-col-full">
                <div className="pd-section-label">Weekly Breakdown</div>
                <div className="pd-week-table-wrap">
                  <table className="pd-week-table">
                    <thead>
                      <tr className="wt-group">
                        <th rowSpan={2} style={{ textAlign: 'left', paddingLeft: 12 }}>
                          Week
                        </th>
                        <th rowSpan={2} style={{ textAlign: 'left' }}>
                          Date
                        </th>
                        <th colSpan={2} className="wt-sep">
                          Volume
                        </th>
                        <th colSpan={6} className="wt-sep">
                          Zones — Duration (hrs)
                        </th>
                        <th colSpan={6} className="wt-sep">
                          Zones — Distance ({distanceUnit})
                        </th>
                      </tr>
                      <tr className="wt-sub">
                        <th className="wt-sep">Hrs</th>
                        <th>{distanceUnit}</th>
                        {ZONE_CONFIGS.map((zone) => (
                          <th
                            key={`dur-${zone.label}`}
                            className={zone.label === 'Z0' ? 'wt-sep' : ''}
                            style={{ color: zone.textColor }}
                          >
                            {zone.label}
                          </th>
                        ))}
                        {ZONE_CONFIGS.map((zone) => (
                          <th
                            key={`dist-${zone.label}`}
                            className={zone.label === 'Z0' ? 'wt-sep' : ''}
                            style={{ color: zone.textColor }}
                          >
                            {zone.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {periodWeeks.map((week) => {
                        const isCurrent = isCurrentWeek(week.week);
                        const durations = week.totalDurationBreakdown ?? [];
                        const distances = week.totalDistanceBreakdown ?? [];

                        return (
                          <tr
                            key={week.week}
                            className={isCurrent ? 'wt-current' : ''}
                            title="View in Training Log"
                            onClick={() => {
                              if (onNavigateToTrainingLog) {
                                onNavigateToTrainingLog(period.index, week.index);
                              } else {
                                navigate(`/analytics/training-log?period=${period.index}&week=${week.index}`);
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="wt-week">
                              Week {week.index}
                              {isCurrent && <span className="wt-current-badge">Current</span>}
                            </td>
                            <td className="wt-date">{formatApiDateDisplay(week.week)}</td>
                            <td className="wt-vol wt-sep">{formatZoneDurationCell(week.duration?.total ?? 0)}</td>
                            <td className="wt-vol-km">{formatZoneDistanceCell(week.distance?.total ?? 0)}</td>
                            {ZONE_CONFIGS.map((zone, zi) => (
                              <td key={`wd-${zi}`} className={`wt-zone ${zi === 0 ? 'wt-sep' : ''}`}>
                                {formatZoneDurationCell(durations[zi] ?? 0)}
                              </td>
                            ))}
                            {ZONE_CONFIGS.map((zone, zi) => (
                              <td key={`wk-${zi}`} className={`wt-zone-km ${zi === 0 ? 'wt-sep' : ''}`}>
                                {formatZoneDistanceCell(distances[zi] ?? 0)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default PeriodAccordion;
